import { Delete } from "@mui/icons-material";
import { Card, CardContent, CardHeader, Typography } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { CreatePestDialog } from "./CreatePestDialog";
import { ConfirmButton } from "../../../../components/Controls/ConfirmButton";
import { ApplicationStandardDataGrid } from "../../../../components/Controls/DataGrid/Standard";
import { useFormTranslator } from "../../../../hooks/useFormTranslator";
import { usePermissions } from "../../../../hooks/usePermissions";
import { Pest } from "../../../../models/company/Pest";
import { Permissions } from "../../../../permissions";
import { useCompanyPestService } from "../../../../service/CompanyService";

export function PestInfo() {
    const { t } = useTranslation();
    const companyService = useCompanyPestService();
    const translator = useFormTranslator("company.pest");

    const permissions = usePermissions();
    const canEdit = permissions.hasPermission(Permissions.CompanyViewManage);

    const columns = useMemo<GridColDef<Pest>[]>(
        () => [
            {
                field: "actions",
                type: "actions",
                getActions: params => [
                    <ConfirmButton
                        key={`company_pest_delete_${params.id}`}
                        confirmText={t("pages.company.pest.warning_remove")}
                        onConfirm={() =>
                            companyService.deletePest(params.row.id)
                        }
                        color="error"
                        icon
                        isDanger
                    >
                        <Delete />
                    </ConfirmButton>,
                ],
                renderHeader: () => (
                    <CreatePestDialog
                        createPestCallback={companyService.createPest}
                    />
                ),
            },
            {
                field: "name",
                headerName: translator.getField("name").getName(),
                flex: 1,
            },
        ],
        [companyService, t, translator],
    );

    return (
        <Card>
            <CardHeader title={t("pages.company.pest.header")} />
            <CardContent>
                <Typography sx={{ paddingBottom: 3 }}>
                    {t("pages.company.pest.description")}
                </Typography>
                <ApplicationStandardDataGrid
                    columns={columns}
                    rows={companyService.pestsResponse.data ?? []}
                    loading={companyService.pestsResponse.isValidating}
                    getRowId={item => item.id}
                    removeColumns={canEdit ? [] : ["actions"]}
                    autoHeight
                    disableRowSelectionOnClick
                    pagination
                    pageSizeOptions={[5]}
                    initialState={{
                        pagination: {
                            paginationModel: {
                                pageSize: 5,
                            },
                        },
                        sorting: {
                            sortModel: [
                                {
                                    field: "name",
                                    sort: "asc",
                                },
                            ],
                        },
                    }}
                />
            </CardContent>
        </Card>
    );
}
